import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Navigate, NavLink, Route, Routes } from 'react-router-dom'
import type { AdminUserListItem, AdminUserListResponse, AdminUserStatus } from '@xingroute/schema'
import { api } from '../api/client.js'
import { useAuth } from '../auth/AuthContext.js'
import { Avatar } from '../components/Avatar.js'
import { Button } from '../components/ui/Button.js'
import { Card } from '../components/ui/Card.js'
import { Input, Select } from '../components/ui/Field.js'
import { useLocale } from '../locale/LocaleContext.js'
import { friendlyError } from '../lib/friendlyError.js'
import { AuthPage } from './AuthPage.js'
import { DiscoverPage } from './DiscoverPage.js'

const PAGE_SIZE = 25

export function AdminPage() {
  const auth = useAuth()
  const { t } = useTranslation('common')
  if (auth.status === 'loading') {
    return <p className="xr-page-container py-8">{t('loading')}</p>
  }
  if (auth.status === 'guest') return <AuthPage mode="admin" />
  if (auth.user?.role !== 'admin') {
    return (
      <div className="xr-page-container py-8">
        <Card>
          <h1 className="text-lg font-semibold">{t('admin.forbiddenTitle')}</h1>
          <p className="mt-2 text-sm text-[var(--xr-text-secondary)]">{t('admin.forbiddenBody')}</p>
        </Card>
      </div>
    )
  }
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex-none border-b border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)]">
        <div className="xr-page-container pt-4">
          <h1 className="text-lg font-semibold">{t('admin.portal')}</h1>
          <nav aria-label={t('admin.navigation')} className="mt-3 flex gap-6">
            {[
              { to: '/admin', label: t('admin.routesTab') },
              { to: '/admin/users', label: t('admin.usersTab') },
            ].map(({ to, label }) => (
              <NavLink
                key={to}
                to={to}
                end
                className={({ isActive }) =>
                  `border-b-2 pb-3 text-sm font-medium ${
                    isActive
                      ? 'border-[var(--xr-brand-primary)] text-[var(--xr-brand-primary)]'
                      : 'border-transparent text-[var(--xr-text-secondary)] hover:text-[var(--xr-text-primary)]'
                  }`
                }
              >
                {label}
              </NavLink>
            ))}
          </nav>
        </div>
      </div>
      <Routes>
        <Route
          index
          element={
            <>
              <div className="xr-page-container flex flex-none flex-wrap items-center gap-x-4 gap-y-2 py-3 text-sm">
                <p className="text-[var(--xr-text-secondary)]">{t('admin.routesDescription')}</p>
                <Link to="/discover" className="text-[var(--xr-brand-primary)] underline">
                  {t('admin.openDiscover')}
                </Link>
              </div>
              <DiscoverPage />
            </>
          }
        />
        <Route path="users" element={<AdminUsersPanel />} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
    </div>
  )
}

function AdminUsersPanel() {
  const auth = useAuth()
  const { t } = useTranslation('common')
  const { locale } = useLocale()
  const [queryInput, setQueryInput] = useState('')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<AdminUserStatus | ''>('')
  const [page, setPage] = useState(1)
  const [state, setState] = useState<{
    loading: boolean
    data: AdminUserListResponse | null
    error: string | null
  }>({ loading: true, data: null, error: null })

  useEffect(() => {
    if (auth.status !== 'authed' || auth.user?.role !== 'admin' || !auth.token) return
    let cancelled = false
    setState((current) => ({ ...current, loading: true, error: null }))
    void api
      .listAdminUsers(
        {
          page,
          pageSize: PAGE_SIZE,
          search: search || undefined,
          status: status || undefined,
        },
        auth.token,
      )
      .then((data) => {
        if (!cancelled) setState({ loading: false, data, error: null })
      })
      .catch((error) => {
        if (!cancelled)
          setState((current) => ({
            loading: false,
            data: current.data,
            error: friendlyError(error, t),
          }))
      })
    return () => {
      cancelled = true
    }
  }, [auth.status, auth.token, auth.user?.role, page, search, status, t])

  const data = state.data
  return (
    <div className="xr-page-container h-full overflow-y-auto py-6" data-testid="admin-users-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="mt-1 text-2xl font-semibold text-[var(--xr-text-primary)]">
            {t('admin.usersTab')}
          </h2>
          <p className="mt-1 text-sm text-[var(--xr-text-secondary)]">
            {t('admin.usersDescription')}
          </p>
        </div>
      </div>

      {data && (
        <div className="mt-5 grid grid-cols-3 gap-3" aria-label={t('admin.summary')}>
          <SummaryCard label={t('admin.statusActive')} value={data.summary.active} />
          <SummaryCard label={t('admin.statusDeleting')} value={data.summary.deleting} />
          <SummaryCard label={t('admin.statusDeleted')} value={data.summary.deleted} />
        </div>
      )}

      <Card className="mt-5">
        <form
          className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_180px_auto]"
          onSubmit={(event) => {
            event.preventDefault()
            setPage(1)
            setSearch(queryInput.trim())
          }}
        >
          <Input
            value={queryInput}
            onChange={(event) => setQueryInput(event.target.value)}
            placeholder={t('admin.searchPlaceholder')}
            aria-label={t('admin.searchLabel')}
            maxLength={100}
          />
          <Select
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as AdminUserStatus | '')
              setPage(1)
            }}
            aria-label={t('admin.statusFilter')}
          >
            <option value="">{t('filterAll')}</option>
            <option value="active">{t('admin.statusActive')}</option>
            <option value="deleting">{t('admin.statusDeleting')}</option>
            <option value="deleted">{t('admin.statusDeleted')}</option>
          </Select>
          <Button type="submit">{t('admin.search')}</Button>
        </form>
      </Card>

      {state.error && (
        <p role="alert" className="mt-4 text-sm text-[var(--xr-danger)]">
          {state.error}
        </p>
      )}
      {state.loading && !data && (
        <p className="mt-6 text-sm text-[var(--xr-text-secondary)]">{t('loading')}</p>
      )}
      {data && data.items.length === 0 && !state.loading && (
        <Card className="mt-5 text-center text-sm text-[var(--xr-text-secondary)]">
          {t('admin.empty')}
        </Card>
      )}
      {data && data.items.length > 0 && (
        <div className="mt-5 space-y-3" aria-busy={state.loading}>
          {data.items.map((user) => (
            <UserCard key={user.id} user={user} locale={locale} />
          ))}
        </div>
      )}

      {data && data.total_pages > 0 && (
        <nav
          className="mt-5 flex items-center justify-between gap-3"
          aria-label={t('admin.pagination')}
        >
          <Button
            type="button"
            variant="secondary"
            disabled={state.loading || page <= 1}
            onClick={() => setPage((value) => Math.max(1, value - 1))}
          >
            {t('admin.previous')}
          </Button>
          <p className="text-sm text-[var(--xr-text-secondary)]">
            {t('admin.pageOf', { page: data.page, pages: data.total_pages, total: data.total })}
          </p>
          <Button
            type="button"
            variant="secondary"
            disabled={state.loading || page >= data.total_pages}
            onClick={() => setPage((value) => value + 1)}
          >
            {t('admin.next')}
          </Button>
        </nav>
      )}
    </div>
  )
}

function SummaryCard({ label, value }: { label: string; value: number }) {
  return (
    <Card padded={false} className="p-4">
      <p className="text-2xl font-semibold tabular-nums text-[var(--xr-text-primary)]">{value}</p>
      <p className="mt-1 text-xs text-[var(--xr-text-secondary)]">{label}</p>
    </Card>
  )
}

function UserCard({ user, locale }: { user: AdminUserListItem; locale: 'zh' | 'en' }) {
  const { t } = useTranslation('common')
  const unavailable = t('admin.unavailable')
  const hasProfile = user.status !== 'deleted'
  const name = user.display_name ?? unavailable
  return (
    <Card data-testid={`admin-user-${user.id}`}>
      <div className="flex items-start gap-3">
        {hasProfile ? (
          <Avatar
            avatarUrl={user.avatar_url}
            seed={user.display_name ?? user.username ?? user.id}
            displayName={name}
            sizeClass="h-12 w-12 text-sm"
            locale={locale}
            testId={`admin-avatar-${user.id}`}
          />
        ) : (
          <div
            className="flex h-12 w-12 flex-none items-center justify-center rounded-full border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface-elev)] text-sm text-[var(--xr-text-muted)]"
            aria-hidden="true"
          >
            —
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="truncate font-semibold text-[var(--xr-text-primary)]">{name}</h2>
            <StatusBadge status={user.status} />
            {user.role === 'admin' && (
              <span className="rounded-full bg-[var(--xr-brand-primary-soft)] px-2 py-0.5 text-xs text-[var(--xr-brand-primary)]">
                {t('admin.roleAdmin')}
              </span>
            )}
          </div>
          <p className="mt-1 break-all text-xs text-[var(--xr-text-secondary)]">
            {user.username ? `@${user.username} · ` : ''}
            {user.id}
          </p>
        </div>
      </div>

      <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-4">
        <Meta
          label={t('admin.registeredAt')}
          value={formatDate(user.created_at, locale, unavailable)}
        />
        <Meta
          label={t('admin.loginMethods')}
          value={
            user.login_methods.length
              ? user.login_methods.map((method) => t(`admin.login.${method}`)).join(' + ')
              : unavailable
          }
        />
        <Meta label={t('admin.level')} value={user.level ? String(user.level) : unavailable} />
        <Meta
          label={t('admin.deletedAt')}
          value={formatDate(
            user.deleted_at ?? user.deletion_started_at,
            locale,
            user.status === 'active' ? '—' : unavailable,
          )}
        />
      </dl>

      {user.stats ? (
        <dl className="mt-4 grid grid-cols-2 gap-2 border-t border-[var(--xr-border-muted)] pt-4 sm:grid-cols-4 lg:grid-cols-7">
          <Stat label={t('admin.statsPublished')} value={user.stats.published_routes} />
          <Stat label={t('admin.statsDrafts')} value={user.stats.draft_routes} />
          <Stat label={t('admin.statsPending')} value={user.stats.pending_review_routes} />
          <Stat label={t('admin.statsProposals')} value={user.stats.proposals} />
          <Stat label={t('admin.statsPhotos')} value={user.stats.photos} />
          <Stat label={t('admin.statsFavorites')} value={user.stats.favorites} />
          <Stat label={t('admin.statsComments')} value={user.stats.comments} />
        </dl>
      ) : (
        <p className="mt-4 border-t border-[var(--xr-border-muted)] pt-4 text-xs text-[var(--xr-text-muted)]">
          {t('admin.deletedDataNotice')}
        </p>
      )}
    </Card>
  )
}

function StatusBadge({ status }: { status: AdminUserStatus }) {
  const { t } = useTranslation('common')
  const tone =
    status === 'active'
      ? 'bg-[var(--xr-success-soft)] text-[var(--xr-success)]'
      : status === 'deleting'
        ? 'bg-[var(--xr-warning-soft)] text-[var(--xr-warning)]'
        : 'bg-[var(--xr-bg-surface-elev)] text-[var(--xr-text-muted)]'
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs ${tone}`}>
      {t(`admin.status.${status}`)}
    </span>
  )
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-[var(--xr-text-muted)]">{label}</dt>
      <dd className="mt-1 text-[var(--xr-text-primary)]">{value}</dd>
    </div>
  )
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <dt className="text-xs text-[var(--xr-text-muted)]">{label}</dt>
      <dd className="mt-1 font-semibold tabular-nums text-[var(--xr-text-primary)]">{value}</dd>
    </div>
  )
}

function formatDate(value: string | undefined, locale: 'zh' | 'en', fallback: string): string {
  if (!value) return fallback
  return new Intl.DateTimeFormat(locale === 'zh' ? 'zh-CN' : 'en', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(new Date(value))
}
